import React, { useMemo } from 'react';
import { Stack } from '@packages/ui/layout';
import { Typography } from '@packages/ui/primitives';
import { resolveFieldDisplayValue, resolveSyncFormatter } from '../utils/resolveFieldDisplayValue';
import { resolveReadonlyFormatter } from '../utils/resolveReadonlyFormatter';
import type { FormLayoutNode } from '../types/layout';
import type { FormFieldMapping } from '../types/fieldMapping';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import { collectFieldsFromLayout } from '../utils/collectLayoutFieldPaths';
import { evaluateFieldState } from '../processors/fieldStateEvaluator';
import { prefixFieldConditions } from '../utils/prefixFieldConditions';

interface FormReviewRendererProps {
  layout: FormLayoutNode[];
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

/**
 * Resolve a display value from live form state, then fall back to per-step
 * snapshots stored in meta (`stepResponses.{stepId}`).
 */
function resolveReviewFieldValue(
  fieldName: string,
  formValues: Record<string, unknown>,
  meta: Record<string, unknown>,
): unknown {
  const fromForm = getNestedValue(formValues, fieldName);
  if (fromForm !== undefined && fromForm !== null && fromForm !== '') {
    return fromForm;
  }

  const fromMetaPath = getNestedValue(meta, fieldName);
  if (fromMetaPath !== undefined && fromMetaPath !== null && fromMetaPath !== '') {
    return fromMetaPath;
  }

  for (const [metaKey, snapshot] of Object.entries(meta)) {
    if (!metaKey.startsWith('stepResponses.') || snapshot == null || typeof snapshot !== 'object') {
      continue;
    }
    const fromStep = getNestedValue(snapshot as Record<string, unknown>, fieldName);
    if (fromStep !== undefined && fromStep !== null && fromStep !== '') {
      return fromStep;
    }
  }

  return fromForm;
}

interface ReviewFieldRowProps {
  field: FormFieldMapping;
  namePrefix?: string;
}

const ReviewFieldRow = React.memo(function ReviewFieldRow({
  field,
  namePrefix,
}: ReviewFieldRowProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { meta, getOptionProp, modeOverrides, blockOverrides } = useFormEngineState();

  const fieldName = namePrefix ? `${namePrefix}.${field.name}` : field.name;
  const prefixedField = useMemo(
    () => prefixFieldConditions(field, namePrefix),
    [field, namePrefix],
  );

  const formValues = form.getValues() as Record<string, unknown>;

  const fieldState = useMemo(
    () =>
      evaluateFieldState(prefixedField, {
        formValues,
        meta,
        getOptionProp,
        isGlobalReadOnly: true,
        modeOverrides,
        blockOverrides,
        namePrefix,
      }),
    [prefixedField, formValues, meta, getOptionProp, modeOverrides, blockOverrides, namePrefix],
  );

  if (!fieldState.visible) {
    return null;
  }

  const rawValue = resolveReviewFieldValue(fieldName, formValues, meta);
  const displayValue = resolveFieldDisplayValue(rawValue, field, {
    selectOptions: fieldState.options ?? field.options,
    formatter: resolveReadonlyFormatter(field, fieldState, getOptionProp),
    addon: field.adapterProps?.endAddon ?? field.adapterProps?.startAddon,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography variant='body-s' style={{ color: 'var(--ui-text-muted)' }}>
        {fieldState.label}
      </Typography>
      <Typography variant='body'>{displayValue}</Typography>
    </div>
  );
});

interface ReviewNodeProps {
  node: FormLayoutNode;
  namePrefix?: string;
}

const ReviewNode = React.memo(function ReviewNode({
  node,
  namePrefix,
}: ReviewNodeProps): React.ReactElement | null {
  switch (node.type) {
    case 'fields':
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '16px 24px',
          }}
        >
          {node.fields.map((entry, idx) => {
            if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'inline-action-button')) return null;
            const field = entry as FormFieldMapping;
            return <ReviewFieldRow key={`${field.name}-${idx}`} field={field} namePrefix={namePrefix} />;
          })}
        </div>
      );

    case 'section':
      return (
        <Stack gap='12px'>
          {node.title ? (
            <Typography variant='body-s' style={{ fontWeight: 600 }}>
              {node.title}
            </Typography>
          ) : null}
          <Stack gap='12px'>
            {node.children.map((child, idx) => (
              <ReviewNode key={idx} node={child} namePrefix={namePrefix} />
            ))}
          </Stack>
        </Stack>
      );

    case 'grid':
    case 'stack':
    case 'surface':
      return (
        <Stack gap='12px'>
          {node.children.map((child, idx) => (
            <ReviewNode key={idx} node={child} namePrefix={namePrefix} />
          ))}
        </Stack>
      );

    case 'tabs':
      return (
        <Stack gap='16px'>
          {node.tabs.map((tab) => (
            <Stack key={tab.id} gap='12px'>
              {tab.label ? (
                  <Typography variant='body-s' style={{ fontWeight: 600 }}>
                    {tab.label}
                  </Typography>
              ) : null}
              {tab.children.map((child, idx) => (
                <ReviewNode key={idx} node={child} namePrefix={namePrefix} />
              ))}
            </Stack>
          ))}
        </Stack>
      );

    case 'conditional':
      return (
        <>
          {node.children.map((child, idx) => (
            <ReviewNode key={idx} node={child} namePrefix={namePrefix} />
          ))}
          {node.else?.map((child, idx) => (
            <ReviewNode key={`else-${idx}`} node={child} namePrefix={namePrefix} />
          ))}
        </>
      );

    case 'each':
      return (
        <Stack gap='16px'>
          {node.itemFields.map((field, idx) => (
            <ReviewFieldRow key={`${field.name}-${idx}`} field={field} namePrefix={namePrefix} />
          ))}
        </Stack>
      );

    case 'column-group':
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${node.groups.length}, 1fr)`, gap: '24px' }}>
          {node.groups.map((group, gIdx) => (
            <Stack key={gIdx} gap='12px'>
              {group.title ? (
                <Typography variant='body-s' style={{ fontWeight: 600 }}>
                  {group.title}
                </Typography>
              ) : null}
              {group.fields.map((entry, fIdx) => {
                if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider' || entry.type === 'inline-action-button')) return null;
                const field = entry as FormFieldMapping;
                return <ReviewFieldRow key={`${field.name}-${fIdx}`} field={field} namePrefix={namePrefix} />;
              })}
            </Stack>
          ))}
        </div>
      );

    default:
      return null;
  }
});

export const FormReviewRenderer = React.memo(function FormReviewRenderer({
  layout,
}: FormReviewRendererProps): React.ReactElement {
  return (
    <Stack gap='20px' style={{ paddingBottom: '20px' }}>
      {layout.map((node, idx) => (
        <ReviewNode key={idx} node={node} />
      ))}
    </Stack>
  );
});
